import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';

import { FlexSpacer } from '@insights-gaming/material-components';
import { createRemFromPx, Theme } from '@insights-gaming/theme';
import { LocalVideoLike, UploadingVideoState } from '@insights-gaming/video-upload-slice';
import { Divider, IconButton, LinearProgress, Typography } from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';

export interface VideoMetadata {
  name: string;
  size: number;
}

interface UploadQueueItemOwnProps {
  className?: string;

  uuid?: string;
  videoMetadata?: VideoMetadata;

  progress?: number;
  completed?: boolean;
  currentEta?: string;
  redirectOnClick?: VoidFunction;
  handlePromoteUpload?: VoidFunction;
  handlePauseCurrentUpload?: VoidFunction;
  completedText?: string;
  etaText?: string;
  abortUploadDialog?: JSX.Element;
  openAbortUploadDialog?: VoidFunction;
  width?: number;
  height?: number;
}

export type UploadQueueItemProps = UploadQueueItemOwnProps;

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    padding: theme.spacing(1, 2, 1, 3),
    '&$completed:hover': {
      cursor: 'pointer',
      backgroundColor: '#353741',
    },
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: ({ width = 150 }: UploadQueueItemProps) => createRemFromPx(width+20),
    marginRight: 'auto',
  },
  progressIndicator: {
    maxWidth: ({ width = 150 }: UploadQueueItemProps) => createRemFromPx(width),
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  progressBar: {
    width: ({ width = 150 }: UploadQueueItemProps) => createRemFromPx(width),
    maxWidth: ({ width = 150 }: UploadQueueItemProps) => createRemFromPx(width),
    height: ({ height = 6 }: UploadQueueItemProps) => createRemFromPx(height),
  },
  cloud: {
    color: theme.palette.success.main,
  },
  divider: {
    margin: theme.spacing(0, 2, 0, 3),
  },
  completed: {},
}), {name: 'UploadQueueItem'});

export function createUploadQueueItem<LV extends LocalVideoLike>(
  getCurrentUpload: (state: any) => UploadingVideoState<LV> | undefined,
): React.FC<UploadQueueItemProps> {
  function UploadQueueItem(props: UploadQueueItemProps) {
    const classes = useStyles(props);
    const {
      className,
      videoMetadata,
      uuid,
      progress,
      handlePauseCurrentUpload,
      completed,
      currentEta,
      redirectOnClick,
      handlePromoteUpload,
      completedText,
      etaText,
      abortUploadDialog,
      openAbortUploadDialog,
    } = props;
    const name = videoMetadata?.name || '';

    const currentUpload = useSelector(getCurrentUpload);

    return (
      <React.Fragment>
        <div
        className={classNames(classes.root, className, {[classes.completed]: completed})}
        onClick={completed ? redirectOnClick : undefined}
        >
          <FlexSpacer flexAlignItems='center' flexJustifyContent='space-between'>
            <Typography noWrap={true} className={classes.name}>
              {name}
            </Typography>
            {completed ? null : currentUpload?.video.uuid !== uuid ? (
              <IconButton size='small' onClick={handlePromoteUpload}>
                <PlayArrowIcon />
              </IconButton>
            ) : (
              <IconButton size='small' onClick={handlePauseCurrentUpload}>
                <PauseIcon/>
              </IconButton>
            )}
            <div className={classes.progressIndicator}>
              <LinearProgress
              variant='determinate'
              value={progress ? progress * 100 : 0}
              className={classes.progressBar}
              />
              <Typography variant='caption'>
                {progress ? progress === 1 ? completedText : `${(progress * 100).toFixed(2)}%` : '0%'}
              </Typography>
              {currentEta && (
                <Typography variant='caption'>
                  {etaText}
                </Typography>
              )}
            </div>
            <IconButton
            size='small'
            onClick={(completed || progress === 1) ? undefined : openAbortUploadDialog}
            disabled={(completed || progress === 1)}
            >
              {(completed || progress === 1)
                ? <CloudDoneIcon className={classes.cloud}/>
                : <CloseIcon htmlColor={red[700]}/>
              }
            </IconButton>
          </FlexSpacer>
          {abortUploadDialog}
        </div>
        <Divider className={classes.divider}/>
      </React.Fragment>
    );
  }

  return React.memo(UploadQueueItem);
}
